import React, { useState, useRef, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import UserContext from '../../components/User_Context.js';
import * as api from '../../api/apiConnector.js';
import * as utils from '../../components/utils.js';
import './EditorJS.css';

const EditorMiniChat = ({ fileId, onDocumentUpdate, editorInstance, onAiChangeAccepted, onAiChangeRejected, pendingAiChanges }) => {
    const { currentUser } = useContext(UserContext);
    const { t } = useTranslation('labels');
    
    // Chat state
    const [history, setHistory] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isQueryEmpty, setIsQueryEmpty] = useState(true);
    const [toAddInTheHistory, setToAddInTheHistory] = useState(null);
    
    // New editor-specific states
    const [documentEditorStarted, setDocumentEditorStarted] = useState(false);
    const [intentInfo, setIntentInfo] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [documentSummary, setDocumentSummary] = useState(null);
    const [processingStatus, setProcessingStatus] = useState(null);
    const [isDocumentModified, setIsDocumentModified] = useState(false);
    
    // New states for function calling system
    const [deprecationWarnings, setDeprecationWarnings] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [executionErrors, setExecutionErrors] = useState([]);
    const [foundBlocks, setFoundBlocks] = useState([]);
    const [detailedErrors, setDetailedErrors] = useState([]);
    
    // Grounding system states
    const [groundingFolders, setGroundingFolders] = useState([]);
    const [selectedGroundingFolders, setSelectedGroundingFolders] = useState([]);
    const [showSettings, setShowSettings] = useState(false);
    const [isApplyingGroundingChanges, setIsApplyingGroundingChanges] = useState(false);
    
    // UI refs
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const isFirstGroundingLoad = useRef(true);
    
    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [history, toAddInTheHistory]);
    
    // Reset selected grounding folders when available folders change
    useEffect(() => {
        if (groundingFolders.length > 0) {
            // Filter out any selected folders that are no longer available
            setSelectedGroundingFolders((prev) =>
                prev.filter((selectedId) =>
                    groundingFolders.some((folder) => folder.Id === selectedId)
                )
            );
        } else {
            // Clear selection if no folders are available
            setSelectedGroundingFolders([]);
        }
    }, [groundingFolders]);
    
    // Restart document editor when grounding settings change
    useEffect(() => {
        // Skip the effect on first load when grounding folders are initially set
        if (isFirstGroundingLoad.current) {
            isFirstGroundingLoad.current = false;
            return;
        }
        
        // Only restart if we have an active chat client and the document editor was already started
        if (window.editorChatClient?.id && documentEditorStarted && fileId) {
            setIsApplyingGroundingChanges(true);
            
            // Debounce to avoid excessive calls when user changes multiple selections quickly
            const timeoutId = setTimeout(async () => {
                try {
                    // Re-initialize the editor with the new grounding file IDs
                    await initializeDocumentEditor(true); 
                } catch (error) {
                    console.error('Error applying grounding changes:', error);
                } finally {
                    // Reset applying state after a short delay to show feedback
                    setTimeout(() => setIsApplyingGroundingChanges(false), 1000);
                }
            }, 500); // 500ms debounce
            
            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [selectedGroundingFolders, documentEditorStarted, fileId]);

    // Auto-add current file to grounding when fileId changes
    useEffect(() => {
        if (fileId && !selectedGroundingFolders.includes(fileId)) {
            setSelectedGroundingFolders(prev => [...prev, fileId]);
        }
    }, [fileId]);

    // Initialize document editor session when fileId changes
    useEffect(() => {
        if (fileId) {
            initializeDocumentEditor(false); // Initial load
        }
        
        return () => {
            // Cleanup chat connection
            if (window.editorChatClient?.emitter) {
                window.editorChatClient.emitter.close();
                window.editorChatClient = null;
            }
        };
    }, [fileId]);

    const startChat = async () => {
        if (!window.editorChatClient) {
            const _client = new window.EventSource(
                `${process.env.REACT_APP_API_URL}/dms/startChat`
            );
            _client.onmessage = (e) => {
                _parseMessage(JSON.parse(e.data));
            };

            window.editorChatClient = {
                emitter: _client,
                html: "",
            };
            return _client;
        }
    };

    const _sendMessage = async (message) => {
        if (window.editorChatClient?.id) {
            message.id = window.editorChatClient.id;
            message.tutor = "AI_PLAN";
            message.file_id = fileId;
            
            // Include grounding folders if selected
            if (selectedGroundingFolders.length > 0) {
                message.grounding_folders = selectedGroundingFolders;
            } else {
                message.grounding_folders = [];
            }
        }

        await api.sendMessage("dms/sendChatMessage", {
            user: currentUser(),
            body: { message },
        });
    };

    const _normalize_chat = () => {
        if (window.editorChatClient && window.editorChatClient.html) {
            if (window.editorChatClient.html.startsWith("```html")) {
                window.editorChatClient.html = window.editorChatClient.html.substring(7);
            }
            if (window.editorChatClient.html.endsWith("``` ") && window.editorChatClient.html.length > 4) {
                window.editorChatClient.html = window.editorChatClient.html.substring(
                    0,
                    window.editorChatClient.html.length - 4
                );
            }
        }
    };

    const _parseMessage = (message) => {
        switch (message.type) {
            case "activity":
                if (message.ai_source?.action === "answer_starting") {
                    window.editorChatClient.starting = true;
                    setIsStreaming(true);
                } else if (message.ai_source?.action === "intent_detected") {
                    setIntentInfo({
                        intent: message.ai_source.intent,
                        confidence: message.ai_source.confidence,
                        method: message.ai_source.method
                    });
                }
                break;
                
            case "intent_detected":
                setIntentInfo({
                    intent: message.intent,
                    confidence: message.confidence,
                    reasoning: message.reasoning
                });
                break;
                
            case "document_editor_started":
                setDocumentEditorStarted(true);
                if (message.documentSummary) {
                    setDocumentSummary(message.documentSummary);
                }
                break;
                
            case "suggestion_added":
                setSuggestions(prev => [...prev, message.suggestion]);
                break;
                
            case "document_summary":
                setDocumentSummary(message.summary);
                break;
                
            case "document_patch":
                // Notify parent component about document changes
                if (onDocumentUpdate && message.documentState) {
                    // Handle both single aiChange (backward compatibility) and aiChanges array
                    if (message.aiChanges && Array.isArray(message.aiChanges)) {
                        // New format: multiple individual changes
                        message.aiChanges.forEach(aiChange => {
                            onDocumentUpdate(message.documentState, aiChange);
                        });
                    } else if (message.aiChange) {
                        // Old format: single change
                        onDocumentUpdate(message.documentState, message.aiChange);
                    } else {
                        // No AI changes, just update document
                        onDocumentUpdate(message.documentState, null);
                    }
                }
                setIsDocumentModified(true);
                break;
                
            case "document_saved":
                setIsDocumentModified(false);
                // Show success notification
                break;
                
            case "ai_change_accepted":
                if (onDocumentUpdate && message.documentState) {
                    onDocumentUpdate(message.documentState, null);
                }
                break;
                
            case "ai_change_rejected":
                if (onDocumentUpdate && message.documentState) {
                    onDocumentUpdate(message.documentState, null);
                }
                break;
                
            case "processing_large_document":
                setProcessingStatus({
                    type: 'large_document',
                    totalBlocks: message.totalBlocks,
                    message: message.message
                });
                break;
                
            case "chunks_identified":
                setProcessingStatus({
                    type: 'chunks_identified',
                    totalChunks: message.totalChunks,
                    relevantChunks: message.relevantChunks,
                    message: message.message
                });
                break;
                
            case "chunk_processed":
                setProcessingStatus(prev => ({
                    ...prev,
                    type: 'chunk_processed',
                    chunkIndex: message.chunkIndex,
                    hasChanges: message.hasChanges,
                    message: message.message
                }));
                break;
                
            case "large_document_updated":
                if (onDocumentUpdate && message.documentState) {
                    onDocumentUpdate(message.documentState);
                }
                setIsDocumentModified(true);
                setProcessingStatus(null);
                break;
                
            case "chat":
                if (window.editorChatClient.starting) {
                    window.editorChatClient.html = "";
                    window.editorChatClient.starting = false;
                }

                window.editorChatClient.html += message.message;
                _normalize_chat();

                // Update the streaming answer display
                const streamingDiv = document.getElementById("editor-streaming-answer");
                if (streamingDiv) {
                    streamingDiv.innerHTML = window.editorChatClient.html;
                }

                if (message.done) {
                    setToAddInTheHistory({
                        id: message.id,
                        question: message.prompt,
                        answer: message.answer,
                        hasChanges: message.hasChanges || false
                    });
                    setIsStreaming(false);
                    setProcessingStatus(null);
                    
                    // If document was modified, show indicator
                    if (message.hasChanges) {
                        setIsDocumentModified(true);
                    }
                }
                break;
                
            case "wellcome":
                window.editorChatClient.id = message.id;
                _sendMessage({
                    type: "authenticate",
                    token: currentUser().token,
                    file_id: fileId,
                });
                break;
                
            case "authenticated":
                if (message.prompts) {
                    message.prompts.sort((a, b) => a.ItemCreated - b.ItemCreated);
                    const history = message.prompts.map((x) => ({
                        id: x.Id,
                        question: x.Prompt,
                        answer: x.Answer,
                        like: x.Like,
                    }));
                    setHistory(utils.clone(history));
                }
                
                // Set grounding folders if available
                if (message.grounding_folders) {
                    setGroundingFolders(utils.clone(message.grounding_folders));
                }
                break;
                
            case "error":
                console.error("Chat error:", message.message);
                setIsStreaming(false);
                setProcessingStatus(null);
                break;
                
            // New Function Calling System Message Types
            case "deprecation_warning":
                setDeprecationWarnings(prev => [...prev, {
                    id: Date.now() + Math.random(),
                    functionName: message.functionName,
                    replacement: message.replacement,
                    reason: message.reason,
                    message: message.message,
                    timestamp: message.timestamp || new Date().toISOString()
                }]);
                console.warn(`Deprecation: ${message.message}`);
                break;
                
            case "validation_error":
                setValidationErrors(prev => [...prev, {
                    id: Date.now() + Math.random(),
                    functionName: message.functionName,
                    errors: message.errors || [],
                    suggestions: message.suggestions || [],
                    appliedFixes: message.appliedFixes || [],
                    message: message.message,
                    timestamp: message.timestamp || new Date().toISOString(),
                    canRetry: true
                }]);
                break;
                
            case "execution_error":
                setExecutionErrors(prev => [...prev, {
                    id: Date.now() + Math.random(),
                    functionName: message.functionName,
                    message: message.message,
                    timestamp: message.timestamp || new Date().toISOString(),
                    canRetry: true
                }]);
                break;
                
            case "blocks_found":
                setFoundBlocks({
                    blocks: message.blocks || [],
                    count: message.count || 0,
                    timestamp: new Date().toISOString()
                });
                // Highlight found blocks in the editor
                if (message.blocks && message.blocks.length > 0) {
                    highlightFoundBlocks(message.blocks);
                }
                break;
                
            case "error_detailed":
                setDetailedErrors(prev => [...prev, {
                    id: message.errorId || Date.now() + Math.random(),
                    errorId: message.errorId,
                    message: message.message,
                    canRetry: message.canRetry || false,
                    recovery: message.recovery,
                    timestamp: message.timestamp || new Date().toISOString()
                }]);
                break;
                
            default:
                break;
        }
    };

    // Utility functions for new message types
    const dismissDeprecationWarning = (warningId) => {
        setDeprecationWarnings(prev => prev.filter(w => w.id !== warningId));
    };

    const dismissValidationError = (errorId) => {
        setValidationErrors(prev => prev.filter(e => e.id !== errorId));
    };

    const dismissExecutionError = (errorId) => {
        setExecutionErrors(prev => prev.filter(e => e.id !== errorId));
    };

    const dismissDetailedError = (errorId) => {
        setDetailedErrors(prev => prev.filter(e => e.id !== errorId));
    };

    const retryLastOperation = async () => {
        // Retry the last user query
        if (currentQuestion) {
            await _sendMessage({
                type: "ai_document_query",
                query: currentQuestion,
            });
        }
    };

    const highlightFoundBlocks = (blocks) => {
        // Remove previous highlights
        document.querySelectorAll('.block-highlighted').forEach(el => {
            el.classList.remove('block-highlighted');
        });

        // Highlight found blocks
        blocks.forEach(block => {
            const element = document.querySelector(`[data-block-id="${block.id}"]`);
            if (element) {
                element.classList.add('block-highlighted');
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    };

    const clearFoundBlocks = () => {
        setFoundBlocks([]);
        document.querySelectorAll('.block-highlighted').forEach(el => {
            el.classList.remove('block-highlighted');
        });
    };
    
    // Utility functions for grounding folders
    const toggleGroundingFolder = (folderId) => {
        setSelectedGroundingFolders((prev) =>
            prev.includes(folderId)
                ? prev.filter((id) => id !== folderId)
                : [...prev, folderId]
        );
    };

    const selectAllGroundingFolders = () => {
        setSelectedGroundingFolders(groundingFolders.map((folder) => folder.Id));
    };

    const clearGroundingFolders = () => {
        setSelectedGroundingFolders([]);
    };

    const initializeDocumentEditor = async (isRestart = false) => {
        if (!isRestart) {
            await startChat();
        }

        // Wait for the chat client to be ready
        const waitForClient = () => new Promise(resolve => {
            const check = () => {
                if (window.editorChatClient?.id) {
                    resolve();
                } else {
                    setTimeout(check, 100);
                }
            };
            check();
        });

        await waitForClient();
        
        console.log(`[FRONTEND DEBUG] Sending start_document_editor. Selected grounding folders: ${JSON.stringify(selectedGroundingFolders)}`);
        
        _sendMessage({
            type: "start_document_editor",
            file_id: fileId,
            token: currentUser().token,
            restart: isRestart
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isQueryEmpty || isStreaming) return;

        const query = textareaRef.current?.value.trim();
        if (!query) return;

        // Clear input
        if (textareaRef.current) {
            textareaRef.current.value = "";
            setIsQueryEmpty(true);
        }

        // Add previous message to history if exists
        if (toAddInTheHistory && toAddInTheHistory.question && toAddInTheHistory.answer) {
            history.unshift(toAddInTheHistory);
            setHistory(utils.clone(history));
            setToAddInTheHistory(null);
        }

        setIsStreaming(true);
        setCurrentQuestion(query);
        setIntentInfo(null);

        // Send AI document query instead of regular query
        await _sendMessage({
            type: "ai_document_query",
            query: query,
        });
    };

    const applySuggestion = async (suggestionId) => {
        await _sendMessage({
            type: "apply_suggestion",
            suggestionId: suggestionId
        });
        
        // Remove suggestion from UI
        setSuggestions(prev => prev.filter(s => s.id !== suggestionId));
    };

    const saveDocument = async () => {
        await _sendMessage({
            type: "save_document"
        });
    };

    const acceptAiChange = async (changeId) => {
        await _sendMessage({
            type: "accept_ai_change",
            changeId: changeId
        });
        
        // Notify parent component
        if (onAiChangeAccepted) {
            onAiChangeAccepted(changeId);
        }
    };

    const rejectAiChange = async (changeId) => {
        await _sendMessage({
            type: "reject_ai_change",
            changeId: changeId
        });
        
        // Notify parent component
        if (onAiChangeRejected) {
            onAiChangeRejected(changeId);
        }
    };

    // UI Components
    const IntentIndicator = () => {
        if (!intentInfo) return null;
        
        const getIntentColor = (intent) => {
            switch (intent) {
                case 'edit': return 'text-warning';
                case 'read': return 'text-info';
                default: return 'text-secondary';
            }
        };
        
        const getIntentIcon = (intent) => {
            switch (intent) {
                case 'edit': return 'fa-edit';
                case 'read': return 'fa-eye';
                default: return 'fa-question';
            }
        };

        return (
            <div className="alert alert-light border-0 intent-indicator">
                <i className={`fas ${getIntentIcon(intentInfo.intent)} ${getIntentColor(intentInfo.intent)} me-2`}></i>
                <strong>Intento rilevato:</strong> {intentInfo.intent === 'edit' ? 'Modifica' : 'Lettura'} 
                <span className="text-muted ms-2">({Math.round(intentInfo.confidence * 100)}%)</span>
            </div>
        );
    };

    const ProcessingStatus = () => {
        if (!processingStatus) return null;

        return (
            <div className="alert alert-info border-0 processing-status">
                <div className="processing-status-content">
                    <div className="spinner-border spinner-border-sm text-info processing-status-spinner" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span>{processingStatus.message}</span>
                </div>
                {processingStatus.type === 'chunks_identified' && (
                    <div className="processing-status-details">
                        <small>Sezioni rilevanti: {processingStatus.relevantChunks}/{processingStatus.totalChunks}</small>
                    </div>
                )}
            </div>
        );
    };

    const SuggestionCard = ({ suggestion }) => (
        <div className="card suggestion-card">
            <div className="card-body suggestion-card-body">
                <div className="suggestion-card-content">
                    <div className="suggestion-card-text">
                        <strong className="text-primary">Suggerimento:</strong>
                        <div className="suggestion-card-reason text-muted">{suggestion.reason}</div>
                    </div>
                    <button
                        className="btn btn-sm btn-outline-primary suggestion-card-button"
                        onClick={() => applySuggestion(suggestion.id)}
                        disabled={isStreaming}
                    >
                        <i className="fas fa-check"></i>
                    </button>
                </div>
            </div>
        </div>
    );

    const DocumentSummaryCard = () => {
        if (!documentSummary) return null;

        return (
            <div className="card document-summary-card">
                <div className="card-header document-summary-header">
                    <h6 className="mb-0">
                        <i className="fas fa-file-alt text-primary me-2"></i>
                        Riassunto Documento
                    </h6>
                </div>
                <div className="card-body document-summary-body">
                    <div className="row document-summary-stats">
                        <div className="col-4">
                            <div className="document-summary-stat-value text-primary">{documentSummary.totalBlocks}</div>
                            <small className="text-muted document-summary-stat-label">Blocchi</small>
                        </div>
                        <div className="col-4">
                            <div className="document-summary-stat-value text-success">{documentSummary.wordCount}</div>
                            <small className="text-muted document-summary-stat-label">Parole</small>
                        </div>
                        <div className="col-4">
                            <div className="document-summary-stat-value text-info">{documentSummary.headers?.length || 0}</div>
                            <small className="text-muted document-summary-stat-label">Titoli</small>
                        </div>
                    </div>
                    {documentSummary.headers && documentSummary.headers.length > 0 && (
                        <div className="document-summary-structure">
                            <strong className="text-muted">Struttura:</strong>
                            <div className="mt-1">
                                {documentSummary.headers.slice(0, 3).map((header, index) => (
                                    <div key={index} className="document-summary-header-item">
                                        {'  '.repeat(header.level - 1)}• {header.text}
                                    </div>
                                ))}
                                {documentSummary.headers.length > 3 && (
                                    <small className="text-muted">... e altri {documentSummary.headers.length - 3}</small>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const UserMessage = ({ message }) => (
        <div className="chat-user-message">
            <div className="bg-primary text-white rounded chat-user-bubble">
                <div className="text-break chat-user-text">{message.question}</div>
                {message.hasChanges && (
                    <div className="mt-1">
                        <small><i className="fas fa-edit me-1"></i>Documento modificato</small>
                    </div>
                )}
            </div>
        </div>
    );

    const AiMessage = ({ message }) => (
        <div className="chat-ai-message">
            <div className="bg-light rounded chat-ai-bubble">
                <div
                    className="text-break chat-ai-text"
                    dangerouslySetInnerHTML={{ __html: message.answer }}
                />
            </div>
        </div>
    );

    const CurrentQuestion = () => (
        <div className="chat-user-message">
            <div className="bg-primary text-white rounded chat-user-bubble">
                <div className="text-break chat-user-text">{currentQuestion}</div>
            </div>
        </div>
    );

    const StreamingMessage = () => (
        <div className="chat-ai-message">
            <div className="bg-light rounded chat-ai-bubble">
                <div
                    id="editor-streaming-answer"
                    className="text-break chat-ai-text"
                ></div>
                {isStreaming && (
                    <div className="typing-indicator mt-2">
                        <span className="dot"></span>
                        <span className="dot"></span>
                        <span className="dot"></span>
                    </div>
                )}
            </div>
        </div>
    );

    const CompletedMessage = () => {
        if (!toAddInTheHistory || !toAddInTheHistory.answer) return null;
        
        return (
            <div className="chat-ai-message">
                <div className="bg-light rounded chat-ai-bubble">
                    <div
                        className="text-break chat-ai-text"
                        dangerouslySetInnerHTML={{ __html: toAddInTheHistory.answer }}
                    />
                </div>
            </div>
        );
    };

    // New UI Components for Function Calling System
    const DeprecationWarningBanner = ({ warning }) => (
        <div className="alert alert-warning deprecation-warning">
            <div className="d-flex justify-content-between align-items-start">
                <div>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Funzione Deprecata:</strong> {warning.functionName}
                    <div className="mt-1">
                        <small>
                            Usa <code>{warning.replacement}</code> invece. {warning.reason}
                        </small>
                    </div>
                </div>
                <button
                    className="btn btn-sm btn-outline-warning"
                    onClick={() => dismissDeprecationWarning(warning.id)}
                    title="Nascondi avviso"
                >
                    <i className="fas fa-times"></i>
                </button>
            </div>
        </div>
    );

    const ValidationErrorModal = ({ error }) => (
        <div className="alert alert-danger validation-error">
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                    <h6 className="alert-heading">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        Parametri Non Validi per {error.functionName}
                    </h6>
                    
                    {error.errors && error.errors.length > 0 && (
                        <div className="validation-error-details">
                            <strong>Problemi rilevati:</strong>
                            <ul className="mb-2">
                                {error.errors.map((err, index) => (
                                    <li key={index}>
                                        <strong>{err.field}:</strong> {err.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    {error.suggestions && error.suggestions.length > 0 && (
                        <div className="validation-error-suggestions">
                            <strong>💡 Suggerimenti:</strong>
                            <ul className="mb-2">
                                {error.suggestions.map((suggestion, index) => (
                                    <li key={index}>{suggestion}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    
                    <div className="mt-2">
                        <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={retryLastOperation}
                            disabled={isStreaming}
                        >
                            <i className="fas fa-redo me-1"></i>
                            Riprova
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => dismissValidationError(error.id)}
                        >
                            Nascondi
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const ExecutionErrorCard = ({ error }) => (
        <div className="alert alert-danger execution-error">
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                    <h6 className="alert-heading">
                        <i className="fas fa-times-circle me-2"></i>
                        Errore di Esecuzione
                    </h6>
                    <p className="mb-2">{error.message}</p>
                    <small className="text-muted">
                        Funzione: {error.functionName} | {new Date(error.timestamp).toLocaleTimeString()}
                    </small>
                    
                    <div className="mt-2">
                        <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={retryLastOperation}
                            disabled={isStreaming}
                        >
                            <i className="fas fa-redo me-1"></i>
                            Riprova
                        </button>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => dismissExecutionError(error.id)}
                        >
                            Nascondi
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const SearchResultsCard = ({ foundBlocks }) => {
        if (!foundBlocks.blocks || foundBlocks.blocks.length === 0) return null;

        return (
            <div className="card search-results-card">
                <div className="card-header search-results-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                            <i className="fas fa-search text-info me-2"></i>
                            Risultati Ricerca ({foundBlocks.count})
                        </h6>
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={clearFoundBlocks}
                            title="Cancella evidenziazioni"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>
                <div className="card-body search-results-body">
                    {foundBlocks.blocks.slice(0, 3).map((block, index) => (
                        <div key={index} className="search-result-item">
                            <div className="search-result-type">
                                <i className={`fas fa-${block.type === 'header' ? 'heading' : 'paragraph'} me-1`}></i>
                                {block.type}
                            </div>
                            <div className="search-result-text">
                                {block.data?.text?.substring(0, 100) || 'Contenuto non disponibile'}
                                {block.data?.text?.length > 100 && '...'}
                            </div>
                        </div>
                    ))}
                    {foundBlocks.count > 3 && (
                        <small className="text-muted">
                            ... e altri {foundBlocks.count - 3} risultati
                        </small>
                    )}
                </div>
            </div>
        );
    };

    const DetailedErrorCard = ({ error }) => (
        <div className="alert alert-info detailed-error">
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                    <h6 className="alert-heading">
                        <i className="fas fa-info-circle me-2"></i>
                        Gestione Errore Avanzata
                    </h6>
                    <p className="mb-2">{error.message}</p>
                    
                    {error.recovery && (
                        <div className={`recovery-status ${error.recovery.success ? 'success' : 'failed'}`}>
                            {error.recovery.success ? (
                                <span className="text-success">
                                    <i className="fas fa-check-circle me-1"></i>
                                    Recupero automatico riuscito: {error.recovery.description}
                                </span>
                            ) : (
                                <span className="text-danger">
                                    <i className="fas fa-times-circle me-1"></i>
                                    Recupero fallito: {error.recovery.description}
                                </span>
                            )}
                        </div>
                    )}
                    
                    <div className="mt-2">
                        {error.canRetry && (
                            <button
                                className="btn btn-sm btn-primary me-2"
                                onClick={retryLastOperation}
                                disabled={isStreaming}
                            >
                                <i className="fas fa-redo me-1"></i>
                                Riprova
                            </button>
                        )}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => dismissDetailedError(error.id)}
                        >
                            Nascondi
                        </button>
                    </div>
                    
                    <small className="text-muted d-block mt-1">
                        ID Errore: {error.errorId} | {new Date(error.timestamp).toLocaleTimeString()}
                    </small>
                </div>
            </div>
        </div>
    );

    // Editor Settings Sidebar Component
    const EditorSettingsSidebar = () => {
        // Handle select all / deselect all
        const handleSelectAll = () => {
            if (selectedGroundingFolders.length === groundingFolders.length) {
                clearGroundingFolders();
            } else {
                selectAllGroundingFolders();
            }
        };

        return (
            <div
                className={`position-fixed d-flex flex-column bg-white border-start border-secondary shadow ${showSettings ? "end-0" : "end-n5"}`}
                style={{
                    top: "0px",
                    height: "100vh",
                    width: "350px",
                    zIndex: 1051,
                    transition: "right 0.3s ease-in-out",
                    right: showSettings ? "0" : "-350px"
                }}
            >
                <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
                    <h5 className="mb-0">
                        <i className="fas fa-cog me-2"></i>
                        {t("settings", { ns: "labels" })}
                    </h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowSettings(false)}
                        aria-label="Close"
                        disabled={isApplyingGroundingChanges}
                    ></button>
                </div>
                <div
                    className="flex-grow-1"
                    style={{ overflowY: "auto", padding: "1rem" }}
                >
                    <div className="row g-3">
                        <div className="col-12">
                            <label className="form-label">
                                {t("grounding", { ns: "labels" })}
                                <span className="text-muted ms-1">
                                    ({selectedGroundingFolders.length}/{groundingFolders.length})
                                </span>
                                {isApplyingGroundingChanges && (
                                    <div className="ms-2 d-inline-block">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Applicando...</span>
                                        </div>
                                        <small className="text-primary ms-1">Applicando...</small>
                                    </div>
                                )}
                            </label>
                            
                            {/* Select All / Deselect All button */}
                            {groundingFolders.length > 0 && (
                                <div className="mb-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={handleSelectAll}
                                        disabled={isApplyingGroundingChanges}
                                    >
                                        {selectedGroundingFolders.length === groundingFolders.length 
                                            ? t("deselectAll", { ns: "labels" }) 
                                            : t("selectAll", { ns: "labels" })}
                                    </button>
                                </div>
                            )}
                            
                            {/* Grounding folders checkboxes */}
                            <div className="mt-2">
                                {groundingFolders.length === 0 ? (
                                    <div className="text-muted fst-italic">
                                        {t("noGroundingFolders", { ns: "labels" })}
                                    </div>
                                ) : (
                                    groundingFolders.map((folder) => (
                                        <div key={folder.Id} className="form-check mb-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`editor-grounding-${folder.Id}`}
                                                checked={selectedGroundingFolders.includes(folder.Id)}
                                                onChange={() => toggleGroundingFolder(folder.Id)}
                                                disabled={isApplyingGroundingChanges}
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor={`editor-grounding-${folder.Id}`}
                                                title={folder.Name}
                                                style={{ opacity: isApplyingGroundingChanges ? 0.6 : 1 }}
                                            >
                                                {folder.Name}
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                            
                            {/* Show selected folders count or applying status */}
                            {isApplyingGroundingChanges ? (
                                <div className="mt-2 text-primary">
                                    <small>
                                        <i className="fas fa-sync fa-spin me-1"></i>
                                        Aggiornando contesto documento...
                                    </small>
                                </div>
                            ) : selectedGroundingFolders.length > 0 ? (
                                <small className="text-muted mt-1 d-block">
                                    {t("selectedFolders", { ns: "labels", count: selectedGroundingFolders.length })}
                                </small>
                            ) : null}
                        </div>
                    </div>
                </div>
                <div className="p-3 border-top bg-light">
                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={() => setShowSettings(false)}
                        disabled={isApplyingGroundingChanges}
                    >
                        {isApplyingGroundingChanges ? (
                            <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Salvando...</span>
                                </div>
                                Applicando...
                            </>
                        ) : (
                            t("save", { ns: "labels" })
                        )}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="editor-mini-chat">
            {/* Settings Sidebar */}
            <EditorSettingsSidebar />
            
            {/* Header */}
            <div className="chat-header">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        <i className="fa-solid fa-robot text-primary me-2"></i>
                        <h6 className="mb-0">AI Editor Assistant</h6>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {/* Settings button */}
                        <button
                            className={`btn btn-sm ${
                                showSettings ? 'btn-primary' : 'btn-outline-secondary'
                            }`}
                            onClick={() => setShowSettings(!showSettings)}
                            title="Impostazioni"
                            disabled={isApplyingGroundingChanges}
                        >
                            <i className={`fas ${
                                isApplyingGroundingChanges ? 'fa-sync fa-spin' : 'fa-cog'
                            }`}></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Messages Area */}
            <div className="chat-messages">
                {/* Document Summary */}
                <DocumentSummaryCard />

                {/* New Function Calling System Components */}
                {/* Deprecation Warnings */}
                {deprecationWarnings.map((warning) => (
                    <DeprecationWarningBanner key={warning.id} warning={warning} />
                ))}

                {/* Validation Errors */}
                {validationErrors.map((error) => (
                    <ValidationErrorModal key={error.id} error={error} />
                ))}

                {/* Execution Errors */}
                {executionErrors.map((error) => (
                    <ExecutionErrorCard key={error.id} error={error} />
                ))}

                {/* Detailed Errors */}
                {detailedErrors.map((error) => (
                    <DetailedErrorCard key={error.id} error={error} />
                ))}

                {/* Search Results */}
                {foundBlocks.blocks && foundBlocks.blocks.length > 0 && (
                    <SearchResultsCard foundBlocks={foundBlocks} />
                )}

                {/* Suggestions */}
                {suggestions.length > 0 && (
                    <div className="mb-3">
                        <h6 className="text-muted mb-2">
                            <i className="fas fa-lightbulb me-2"></i>Suggerimenti
                        </h6>
                        {suggestions.map((suggestion) => (
                            <SuggestionCard key={suggestion.id} suggestion={suggestion} />
                        ))}
                    </div>
                )}

                {/* Welcome message when no history */}
                {history.length === 0 && !currentQuestion && !isStreaming && (
                    <div className="chat-welcome">
                        <i className="fa-solid fa-robot text-primary chat-welcome-icon"></i>
                        <p className="text-muted chat-welcome-text">
                            Ciao! Sono il tuo assistente AI per l'editing.
                        </p>
                        <p className="text-muted chat-welcome-subtext">
                            Posso aiutarti a modificare, formattare e migliorare il documento.
                        </p>
                    </div>
                )}

                {/* Previous Messages */}
                {[...history].reverse().map((message, index) => (
                    <div key={message.id || index}>
                        <UserMessage message={message} />
                        <AiMessage message={message} />
                    </div>
                ))}

                {/* Current conversation */}
                {(currentQuestion || isStreaming) && (
                    <div>
                        {currentQuestion && <CurrentQuestion />}
                        <IntentIndicator />
                        <ProcessingStatus />
                        {isStreaming && <StreamingMessage />}
                        {toAddInTheHistory && !isStreaming && <CompletedMessage />}
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="chat-input">
                <form onSubmit={handleSubmit}>
                    <div className="d-flex gap-2">
                        <textarea
                            ref={textareaRef}
                            className="form-control chat-input-textarea"
                            placeholder="Dimmi cosa vuoi modificare nel documento..."
                            rows="2"
                            onChange={(e) => setIsQueryEmpty(e.target.value.trim() === "")}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey && !isQueryEmpty && !isStreaming) {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }
                            }}
                            disabled={isStreaming}
                        />
                        <button
                            type="submit"
                            className="btn btn-primary chat-input-button"
                            disabled={isQueryEmpty || isStreaming}
                        >
                            <i className="fa-solid fa-paper-plane"></i>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditorMiniChat; 